// Client_Directory_Dashboard.js — Redesigned with blue client theme
import { Box, Button, Grid, Paper, Slider, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiBarChart2, FiCheckCircle, FiClock, FiDollarSign, FiWind } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/container/MainContainer";
import SelectBox from "../../components/input/SelectBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import routing from "../../routing";
import "../../style/pages-style/Engagement/engagement.css";
import MetricCard from "../../app_components/MetricCard";
import PivotablePieChart from "../../components/chart/PivotablePieChart";
import ScatterChart from "../../components/chart/ScatterChart";
import ReportDetailGrid from "./Dashboard/ReportDetailGrid";
import ReportGrid from "./Dashboard/ReportGrid";
import EngagementDashboardGlobalView from "../Engagement/EngagementDashboardGlobalView";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";

/* ─── Metric card config ─────────────────────────────────────────── */
const METRIC_CONFIG = [
  { key: "clientCount", title: "Clients", icon: FiCheckCircle, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.08)" },
  { key: "reportSentCount", title: "Reports Sent", icon: FiBarChart2, accent: "#7c3aed", bg: "rgba(124, 58, 237, 0.08)" },
  { key: "pendingCount", title: "Pending Response", icon: FiClock, accent: "#d97706", bg: "rgba(217, 119, 6, 0.08)" },
  { key: "gwpSent", title: "GWP Sent", icon: FiWind, accent: "#0891b2", bg: "rgba(8, 145, 178, 0.08)", unit: "t CO₂-eq" },
  { key: "costSent", title: "Cost Sent", icon: FiDollarSign, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.08)", unit: "₹" },
];

const emission = { label: "Emission", value: "Emission", company_field: "gwp", report_field: "gwp_per_unit" };
const cost = { label: "Cost", value: "Cost", company_field: "cost", report_field: "cost_per_unit" };
const score = { label: "Score", value: "Score", company_field: "score", report_field: "score" };
const comparisonOptions = [emission, cost, score];

const Client_Directory_Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  // ── State Hooks ─────────────────────────────────────────────
  const [globalView, setGlobalView] = useState(false);
  const [comparison, setComparison] = useState(emission.value);
  const [reports, setReports] = useState([]);
  const [acceptedReports, setAcceptedReports] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [optionCompanies, setOptionCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [optionYears, setOptionYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [emissionWeight, setEmissionWeight] = useState(50);
  const [type] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [sentReports, setSentReports] = useState([]);
  const [receivers, setReceivers] = useState([]);

  const costWeight = 100 - emissionWeight;

  useEffect(() => { setPendingReports(reports.filter((r) => r?.status === "Pending")); }, [reports]);
  
  useEffect(() => {
    let temp = [...reports];
    if (selectedCompany) temp = temp.filter((r) => r?.receiver_id === selectedCompany);
    if (selectedYear) temp = temp.filter((r) => r?.year === selectedYear);
    if (type) temp = temp.filter((r) => r?.type === type);
    setAcceptedReports(temp);
  }, [reports, selectedCompany, selectedYear, type]);

  useEffect(() => {
    Supplier_Reports.get_mine_sent().then((r) => r.json()).then((data) => {
      setReports(data);
      setSentReports(data);
    });
  }, [refresh]);

  useEffect(() => {
    const unique = reports.reduce((acc, r) => {
      if (!acc.some((c) => c.value === r.receiver_id)) acc.push({ label: r.receiver_name, value: r.receiver_id });
      return acc;
    }, []);
    setOptionCompanies(unique);
  }, [reports]);

  useEffect(() => {
    setOptionYears(reports.reduce((acc, r) => {
      if (!acc.some((c) => c.value === r.year)) acc.push({ label: String(r.year), value: r.year });
      return acc;
    }, []));
  }, [reports]);

  useEffect(() => {
    const temp = new Map();
    const getScore = (r) => (emissionWeight * (r.gwp || 0) + costWeight * (r.cost || 0)) / 100;
    acceptedReports.forEach((report) => {
      const sc = getScore(report);
      if (temp.has(report.receiver_id)) {
        const ex = temp.get(report.receiver_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0;
        ex.reports.push({ ...report, score: sc }); ex.score += sc;
      } else {
        temp.set(report.receiver_id, {
          name: report.receiver_name, id: report.receiver_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [{ ...report, score: sc }], score: sc
        });
      }
    });
    let arr = Array.from(temp.values());
    const co = comparisonOptions.find((c) => c.value === comparison);
    if (co) {
      arr = arr.sort((a, b) => (a[co.company_field] > b[co.company_field] ? 1 : -1));
      arr.forEach((item) => { if (Array.isArray(item.reports)) item.reports.sort((r1, r2) => r1[co.report_field] - r2[co.report_field]); });
    }
    setCompanies(arr);
  }, [acceptedReports, comparison, emissionWeight, costWeight]);

  useEffect(() => {
    const temp = new Map();
    sentReports.forEach((report) => {
      if (temp.has(report.receiver_id)) {
        const ex = temp.get(report.receiver_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0; ex.reports.push(report);
      } else {
        temp.set(report.receiver_id, {
          name: report.receiver_name, id: report.receiver_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [report]
        });
      }
    });
    setReceivers(Array.from(temp.values()));
  }, [sentReports]);

  /* ── computed metrics ─────────────────────────────────────────── */
  const metrics = useMemo(() => {
    // Dynamic selection support
    const activeReports = selectedCompanyIds.length > 0 
      ? sentReports.filter(r => selectedCompanyIds.includes(r.receiver_id))
      : sentReports;
    
    const activeReceivers = selectedCompanyIds.length > 0
      ? receivers.filter(s => selectedCompanyIds.includes(s.id))
      : receivers;

    const activePending = selectedCompanyIds.length > 0
      ? pendingReports.filter(r => selectedCompanyIds.includes(r.receiver_id))
      : pendingReports;

    return {
      clientCount: activeReceivers.length,
      reportSentCount: activeReports.length,
      pendingCount: activePending.length,
      gwpSent: (activeReports.reduce((s, r) => s + (r.gwp || 0), 0) / 1000).toFixed(2),
      costSent: activeReports.reduce((s, r) => s + (r.cost || 0), 0).toFixed(2),
    };
  }, [receivers, sentReports, pendingReports, selectedCompanyIds]);

  return (
    <MainContainer>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: ORG_PALETTE.spacing.metricCardGap, mb: ORG_PALETTE.spacing.pageGap }}>
        {METRIC_CONFIG.map((m, i) => (
          <MetricCard
            key={m.key}
            title={t(m.title)}
            value={metrics[m.key]}
            unit={m.unit}
            icon={m.icon}
            accent={m.accent}
            bg={m.bg}
            delay={i * 0.1}
          />
        ))}
      </Box>

      <Box sx={{ mb: 3 }}>
          {companies.length === 0 ? (
            <Paper variant="outlined" sx={{ borderRadius: "16px", border: "1.5px dashed #e5e7eb" }}>
              <div className="eng-empty-state">
                <div className="eng-empty-state__icon">📤</div>
                <div className="eng-empty-state__title">{t("No clients yet")}</div>
                <div className="eng-empty-state__subtitle">
                  {t("Submit data to one of your B2B partners to start tracking them as a client here")}
                </div>
                <Button
                  variant="contained"
                  endIcon={<FiArrowRight />}
                  onClick={() => navigate(routing.engagement.client_directory.submit_data)}
                  sx={{
                    mt: 1, backgroundColor: "#2563eb", "&:hover": { backgroundColor: "#1d4ed8" },
                    fontFamily: "Poppins", fontSize: 13, fontWeight: 600,
                    borderRadius: "10px", textTransform: "none",
                  }}>
                  {t("Submit Your First Report")}
                </Button>
              </div>
            </Paper>
          ) : (
            <ReportGrid companies={companies} title={t("Clients")} selectedCompanyIds={selectedCompanyIds} setSelectedCompanyIds={setSelectedCompanyIds} globalView={globalView} setGlobalView={setGlobalView} />
          )}
      </Box>

      {!globalView && selectedCompanyIds.length > 0 && (
        <Box sx={{ mt: 3, animation: "eng-fadeInUp 0.4s ease both" }}>
          <ReportDetailGrid companies={companies} selectedCompanyIds={selectedCompanyIds} setSelectedCompanyIds={setSelectedCompanyIds} showAddButton={false} />
        </Box>
      )}

      {globalView && (
        <Box sx={{ animation: "eng-fadeInUp 0.4s ease both", mt: 4 }}>
          <EngagementDashboardGlobalView
            companies={companies}
            entityLabel="Clients"
            accentColor="#2563eb"
          />
        </Box>
      )}
    </MainContainer>
  );
};

export default Client_Directory_Dashboard;
